/* eslint-disable react/prop-types */
import moment from 'moment';
import React from 'react';
import { useTheme } from 'styled-components';
import StatusPill from '../StatusPill';
import { tokens, inkA } from '../tokens';

// Gen-3 event-row status: a stacked pair of pills — a lifecycle pill (Live /
// Draft / Cancelled / Postponed / Completed) and, unless we're on drafts, a
// sale pill (On Sale Set / On Sale / Off Sale). Composes the shared StatusPill;
// tones mirror the design prototype's CHIP_TONES.

// Tone -> StatusPill color/background/border. `gray` resolves against the
// active theme's ink so it works in light and dark.
const TONES = {
  green: {
    color: '#1F9D57',
    background: 'rgba(52,210,123,0.12)',
    border: 'rgba(52,210,123,0.32)',
  },
  blue: {
    color: '#2C6FD6',
    background: 'rgba(59,130,246,0.14)',
    border: 'rgba(59,130,246,0.32)',
  },
  amber: {
    color: '#B97509',
    background: 'rgba(245,158,11,0.13)',
    border: 'rgba(245,158,11,0.34)',
  },
  red: {
    color: '#D63A24',
    background: 'rgba(240,86,62,0.12)',
    border: 'rgba(240,86,62,0.32)',
  },
  violet: {
    color: '#7C4DDB',
    background: 'rgba(169,139,255,0.14)',
    border: 'rgba(169,139,255,0.34)',
  },
};

const grayTone = (theme) => ({
  color: tokens(theme).sub,
  background: inkA(theme, 0.05),
  border: inkA(theme, 0.14),
});

const parse = (d) => moment(d, 'MM-DD-YYYY');

// Lifecycle pill from the event's stored status + list kind. Backend event
// statuses are: draft, published, cancelled, postponed.
const lifecycle = (event, kind) => {
  const status = event && event.status;
  if ('draft' === status) return { label: 'Draft', tone: 'amber' };
  if ('cancelled' === status) return { label: 'Cancelled', tone: 'red' };
  if ('postponed' === status) return { label: 'Postponed', tone: 'violet' };
  if ('past' === kind) return { label: 'Completed', tone: 'gray' };
  return { label: 'Live', tone: 'green' };
};

// Sale pill. `tickets_onsale` is the backend's per-ticket check (ticket
// windows fall back to the event window) restricted to the event's own
// tickets — annual/season passes are excluded so they don't mark every event
// on sale. When not selling yet, `next_sale_start` carries the earliest
// upcoming ticket on-sale date, so the pill can say when sales open.
// Null = render nothing.
const saleChip = (event) => {
  if (event.tickets_onsale) return { label: 'On Sale', tone: 'blue' };

  const upcomingLabel = (m) => ({
    label: `On Sale ${m.format(
      m.year() === moment().year() ? 'MMM D' : 'MMM D, YYYY'
    )}`,
    tone: 'amber',
  });

  const next = parse(event.next_sale_start);
  if (next.isValid()) return upcomingLabel(next);

  const start = parse(event.sale_start);
  if (!start.isValid()) return null;

  if (moment().isBefore(start)) return upcomingLabel(start);
  return { label: 'Off Sale', tone: 'gray' };
};

const Pill = ({ label, tone, theme }) => {
  const m = 'gray' === tone ? grayTone(theme) : TONES[tone] || grayTone(theme);
  return (
    <StatusPill
      label={label}
      color={m.color}
      background={m.background}
      border={m.border}
    />
  );
};

// Past events swap the sale pill for the payout state once the payout is
// marked green ("Paid" per the design); otherwise they keep the sale pill.
const pastChip = (event) =>
  'green' === event.payout_status
    ? { label: 'Paid', tone: 'green' }
    : saleChip(event);

const secondChip = (event, kind) => {
  if ('drafts' === kind) return null;
  if ('past' === kind) return pastChip(event);
  return saleChip(event);
};

const EventStatusChips = ({ event, kind, direction = 'column' }) => {
  const theme = useTheme();
  if (!event) return null;

  const life = lifecycle(event, kind);
  const sale = secondChip(event, kind);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: direction,
        gap: 6,
        alignItems: direction === 'column' ? 'flex-start' : 'center',
        flexWrap: 'wrap',
      }}
    >
      <Pill label={life.label} tone={life.tone} theme={theme} />
      {sale ? <Pill label={sale.label} tone={sale.tone} theme={theme} /> : null}
    </div>
  );
};

export default EventStatusChips;
